import React from "react";

import { DocumentIcon, Upload, XIcon } from "../../../assets/icons/interface/interface";
import { Button } from "../../JobsPage/components/Button";

export type SupportedFile = {
	name: string;
	extension: string;
	mimeType: string;
};

type FileUploadProps = {
	processDataOfUploadedFile?: (file: File | File[], index?: number) => void;
	removeFile?: () => void;
	fileName?: string;
	multiple?: boolean;
	index?: number;
	fileError?: string | null;
	setFileError: (error: string | null) => void;
	supportedFiles: SupportedFile[];
	disableFileInput?: boolean;
};

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

function FileUpload({
	processDataOfUploadedFile,
	removeFile,
	fileName,
	multiple,
	index,
	fileError,
	setFileError,
	supportedFiles,
	disableFileInput,
}: FileUploadProps) {
	const [isDragOver, setIsDragOver] = React.useState(false);
	const inputRef = React.useRef<HTMLInputElement>(null);

	const supportedFormatsLabel = supportedFiles.map((supportedFile) => supportedFile.extension).join(", ");

	// Returns the first validation failure, or null when every file is acceptable.
	const findValidationError = (files: File[]): string | null => {
		for (const file of files) {
			if (file.size > MAX_FILE_SIZE) {
				return `File size for ${file.name} exceeds 2MB`;
			}

			if (!supportedFiles.some((supportedFile) => supportedFile.mimeType === file.type)) {
				return `Invalid file type for ${file.name}. Only ${supportedFormatsLabel} are allowed.`;
			}
		}

		return null;
	};

	// Single entry point for both the picker and drag/drop, so neither can skip validation.
	const acceptFiles = (fileList: FileList | null) => {
		if (!processDataOfUploadedFile) return;

		const files = Array.from(fileList ?? []);

		// The picker was dismissed, or the drop carried no files. Leave state untouched.
		if (files.length === 0) return;

		const validationError = findValidationError(files);

		if (validationError) {
			setFileError(validationError);
			return;
		}

		setFileError(null);
		processDataOfUploadedFile(multiple ? files : files[0], index);
	};

	const readFile = (event: React.ChangeEvent<HTMLInputElement>) => {
		acceptFiles(event.target.files);

		// Clear the input so re-picking the same file still fires onChange after an error.
		event.target.value = "";
	};

	const handleOnDragOver = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragOver(true);
	};

	const handleOnDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragOver(false);
	};

	const handleOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragOver(false);

		acceptFiles(event.dataTransfer.files);
	};

	const onClick = () => {
		inputRef.current?.click();
	};

	return (
		<div className="mb-4">
			{fileName ? (
				<div
					className={`relative w-full h-10 flex items-center group border border-dashed border-gray-200 hover:border-gray-300 my-3 rounded-lg transition-colors ${
						fileName ? "bg-primary-50 border-primary-200 border-solid" : ""
					}`}
				>
					<div className="w-full h-full flex items-center px-3 pr-20">
						<DocumentIcon className="h-4 w-4 text-primary-500 mr-2 flex-shrink-0" />
						<span className="text-[13px] font-medium text-primary-700 truncate">{fileName}</span>
						<div className="absolute right-2 flex items-center space-x-1">
							<Button
								type="button"
								variant="ghost"
								className="h-8 w-8 p-0 text-primary-500 hover:text-primary-700"
								onClick={removeFile}
							>
								<XIcon className="h-4 w-4" />
							</Button>
						</div>
					</div>
				</div>
			) : (
				<div
					className={`border-2 border-dashed rounded-lg p-6 transition-colors hover:border-primary-500/50 hover:bg-gray-50 ${isDragOver && "border-2 border-solid border-primary-500 transition-all ease-in-out"}`}
					onDragOver={handleOnDragOver}
					onDragLeave={handleOnDragLeave}
					onDrop={handleOnDrop}
				>
					{!disableFileInput && (
						<div className="flex flex-col items-center justify-center space-y-3 text-center" onClick={onClick}>
							<div className="p-2 bg-primary-50 rounded-full">
								<Upload className="w-10 h-10 text-primary-600" />
							</div>
							<div>
								<p className="text-gray-600 text-sm">Drag and drop your file here, or</p>
								<Button variant="link" className="text-primary-600 text-sm h-auto p-0">
									click to browse
								</Button>
							</div>
							<p className="text-xs text-gray-500">
								Supported formats: {supportedFiles.map((supportedFile) => supportedFile.name).join(", ")} (Max size:
								2MB)
							</p>
							<input
								disabled={disableFileInput}
								multiple={multiple}
								type="file"
								accept={supportedFiles.map((supportedFile) => supportedFile.extension).join(",")}
								className="hidden"
								id="uploadFile"
								ref={inputRef}
								onChange={readFile}
							/>
						</div>
					)}
				</div>
			)}
			<div className="text-red-500">{fileError}</div>
		</div>
	);
}

export default FileUpload;
